/**
 * Repositorio de "Mejoras PMIS" — feedback cross-gerencia del propio
 * sistema (hallazgos, observaciones, recomendaciones registradas por
 * cualquier usuario mientras prueba el PMIS).
 *
 * Diseño:
 *   - Sin scope por gerencia: el feedback es del sistema, no del proyecto.
 *     Todos ven todo lo registrado.
 *   - Cualquier usuario logueado puede `create`.
 *   - Solo el ADMIN_PMIS puede `responder` (escribir respuesta + cambiar
 *     estado). La autorización se controla en la UI.
 *   - Sin `update` genérico — toda mutación pasa por `create` o `responder`
 *     para mantener trazabilidad.
 */

import type { MejoraPmis, EstadoMejora } from "@app-types/entities";

export interface RespuestaAdminInput {
  respuestaAdmin: string;
  estado: EstadoMejora;
}

/**
 * Calcula la fecha de cierre (ISO YYYY-MM-DD) de una mejora al responder:
 *   - si el nuevo estado es CERRADO, conserva la fecha de cierre previa (cuando
 *     ya estaba cerrada) o usa `hoy` en el primer cierre;
 *   - si el estado deja de ser CERRADO (reapertura), devuelve `undefined` para
 *     que la fecha de cierre se limpie.
 * Función pura (recibe `hoy`) para poder probarla sin repos.
 */
export function calcularFechaCierre(
  estado: EstadoMejora,
  fechaCierrePrevia: string | undefined,
  hoy: string,
): string | undefined {
  if (estado !== "CERRADO") return undefined;
  return fechaCierrePrevia ?? hoy;
}

export interface MejoraRepo {
  list(): Promise<MejoraPmis[]>;
  get(id: string): Promise<MejoraPmis | null>;
  create(
    data: Omit<
      MejoraPmis,
      | "id"
      | "tenantId"
      | "createdAt"
      | "updatedAt"
      | "version"
      | "createdBy"
      | "updatedBy"
      | "estado"
      | "respuestaAdmin"
      | "fechaRespuesta"
      | "respondidoPor"
    >,
    userEmail: string,
  ): Promise<string>;
  /**
   * Registrar respuesta del admin y cambiar estado. Solo el ADMIN_PMIS
   * debe poder invocarlo — la UI controla el gating.
   */
  responder(
    id: string,
    input: RespuestaAdminInput,
    adminEmail: string,
  ): Promise<void>;
  delete(id: string): Promise<void>;
}
